import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type ClientSummary } from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { Banner } from '../components/Banner';

function relative(iso: string | null): string {
  if (!iso) return '—';
  const secs = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
}

export function Clients() {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.clients()
      .then((r) => setClients(r.clients))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load clients.'));
  }, []);

  const connectedCount = (clients ?? []).filter((c) => c.quickbooks?.status === 'connected').length;

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!clients) return <p className="spinner-text">Loading clients…</p>;

  // An empty list has two very different causes and the user cannot tell them
  // apart from a blank table.
  if (clients.length === 0) {
    return (
      <>
        <div className="page-head">
          <h1>Clients</h1>
        </div>
        <div className="card">
          <div className="empty">
            <h2>No clients yet</h2>
            <p className="muted" style={{ maxWidth: '30rem', margin: '0 auto' }}>
              Either this firm has no clients, or your account has not been granted
              access to any. Clients are set up by Fiscal Founders.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Clients</h1>
        <p className="lede muted" style={{ margin: 0 }}>
          {clients.length} client{clients.length === 1 ? '' : 's'} in this firm.
          {' '}{connectedCount} connected to QuickBooks.
        </p>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>QuickBooks</th>
              <th>Last successful read</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/clients/${c.id}`)}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/clients/${c.id}`); }}
              >
                <td>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.quickbooks?.companyName && c.quickbooks.companyName !== c.name && (
                    <div className="small muted">QuickBooks: {c.quickbooks.companyName}</div>
                  )}
                </td>
                <td><StatusBadge status={c.quickbooks?.status ?? null} /></td>
                <td className="small muted tnum">{relative(c.quickbooks?.lastSuccessAt ?? null)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
